import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  IconButton,
} from '@material-ui/core';
import { Edit, Delete } from '@material-ui/icons';
import { useTranslation } from 'react-i18next';

import { getSitefConfigs, deleteSitefConfig } from 'services/sitefServices';
import PaginationData from 'types/models/PaginationData';
import EditSitefConfigContext from 'contexts/EditSitefConfigContext';
import AppContext from 'contexts/AppContext';
import SitefConfig from 'types/models/SitefConfig';
import ConfirmDialog from 'components/ConfirmDialog';
import UserRoleType from 'types/enums/UserRoleType';

export default (): JSX.Element => {
  const { t } = useTranslation();
  const history = useHistory();
  const { setSelectedSitefConfig } = React.useContext(EditSitefConfigContext);
  const { authData } = React.useContext(AppContext);
  const [paginationSitefConfig, setPaginationSitefConfigs] = useState<PaginationData<SitefConfig> | null>(null);
  const [openDialog, setOpenDialog] = React.useState(false);
  const [sitefIdToDelete, setSitefIdToDelete] = React.useState<string | null>(null);
  const isVenueEditor = authData?.user?.role === UserRoleType.VenueEditor;

  const goToPage = (url: string): void => {
    history.push(url);
  };

  React.useEffect(() => {
    loadData();
  }, []);

  const loadData = React.useCallback(async (page?: number) => {
    const paginationData = await getSitefConfigs(page, isVenueEditor);
    if (paginationData) setPaginationSitefConfigs(paginationData);
  }, []);

  const onDeleteSitefConfig = async () => {
    if (sitefIdToDelete) {
      await deleteSitefConfig(sitefIdToDelete);
      history.go(0);
    }
  };

  const onCloseDialog = () => {
    setSitefIdToDelete(null);
  };

  const renderEditButton = (sitefConfig: SitefConfig): JSX.Element => {
    return (
      <IconButton
        color="inherit"
        onClick={() => {
          setSelectedSitefConfig(sitefConfig);
          history.push('sitef/edit');
        }}
        disabled={isVenueEditor}
      >
        <Edit />
      </IconButton>
    );
  };

  const renderDeleteButton = (sitefConfig: SitefConfig | null): JSX.Element => {
    if (isVenueEditor) return <span>-</span>;
    return (
      <IconButton
        color="inherit"
        onClick={() => {
          if (!sitefConfig) return;
          setOpenDialog(true);
          setSitefIdToDelete(sitefConfig.id);
        }}
      >
        <Delete />
      </IconButton>
    );
  };

  const renderContent = (paginationData: SitefConfig[]): JSX.Element => {
    return (
      <>
        {paginationData.map((sitefConfig: SitefConfig) => (
          <TableRow key={sitefConfig.id}>
            <TableCell component="th" scope="row">
              {sitefConfig.description}
            </TableCell>
            <TableCell>{renderEditButton(sitefConfig)}</TableCell>
            <TableCell>{renderDeleteButton(sitefConfig)}</TableCell>
          </TableRow>
        ))}
      </>
    );
  };

  return (
    <div>
      <ConfirmDialog
        open={openDialog && sitefIdToDelete !== null}
        setOpen={setOpenDialog}
        onConfirm={onDeleteSitefConfig}
        onCancel={onCloseDialog}
      />
      <div className="vertical-bottom-space">
        <Button className="btn-50" variant="contained" color="primary" onClick={() => goToPage('sitef/create')}>
          {t('listSitefConfigs-page-create')}
        </Button>
      </div>
      <div className="vertical-bottom-space">
        <Button className="btn-50" variant="contained" color="secondary" onClick={() => history.go(-1)}>
          {t('go-back')}
        </Button>
      </div>
      <TableContainer className="table-container" component={Paper}>
        <Table>
          <TableHead className="table-header">
            <TableRow>
              <TableCell>{t('listSitefConfigs-page-name')}</TableCell>
              <TableCell>{t('listSitefConfigs-page-edit')}</TableCell>
              <TableCell>{t('listSitefConfigs-page-delete')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginationSitefConfig?.data && paginationSitefConfig?.data.length > 0 ? (
              renderContent(paginationSitefConfig.data)
            ) : (
              <TableRow>
                <TableCell component="th" scope="row">
                  {t('listSitefConfigs-page-noConfigs')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {paginationSitefConfig && (
        <TablePagination
          component="div"
          count={paginationSitefConfig.total}
          page={paginationSitefConfig.currentPage - 1}
          onPageChange={(_, page: number) => {
            loadData(page + 1);
          }}
          rowsPerPage={paginationSitefConfig.perPage}
          rowsPerPageOptions={[]}
          labelDisplayedRows={({ from, to, count }) => {
            return `${from}-${to} ${t('pagination-of')} ${count !== -1 ? count : '0'}`;
          }}
        />
      )}
    </div>
  );
};
